import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRouteMatch } from "react-router-dom";
import { inject, observer } from "mobx-react";

import Drawer from "@material-ui/core/Drawer";

import useChatCompletion from "./hooks/useChatCompletion";
import useChatConfig from "./hooks/useChatConfig";
import useChatSettings from "./hooks/useChatSettings";

import {
  CHAT_CONVERSATON_ORG_USED_HEADER,
  OPENAI_MODELS,
  OPENAI_MODEL_VARIANTS,
} from "./lib/constants";

import {
  generateUUID,
  setKeyValueInLocalStorage,
  getValueFromLocalStorage,
} from "./lib/utils";

import { getLogoSrc, getTitle } from "app/common/utils/Logo";

import ChatDrawerHeader from "./ChatDrawerHeader";
import ChatDrawerHome from "./ChatDrawerHome";
import ChatSettings from "./ChatSettings";
import ChatHistory from "./ChatHistory";

import assistantConfig from "./configs/assistant.config";
// import res from "./configs/res.json";

import RequestLimitError from "./classes/RequestLimitError";

import { useStyles } from "./styles";

const ChatDrawer = (props) => {
  const {
    chatStore,
    assistantStore,
    chatFnsStore,
    chatHelpersStore,
    chatTaskStore,
    chatSettingsStore,
    packStore,
  } = props;

  const inputRef = useRef(null);

  const routeMatch = useRouteMatch(
    "/pack/:packIdFromRoute/list/:sheetIdFromRoute"
  );

  const classes = useStyles();

  const inputProxy = getValueFromLocalStorage("inputProxy");

  const [tokensState, setTokensState] = useState(null);

  const { settings, apiKey, updateOpenAIApiKey, removeOpenAIApiKey } =
    useChatSettings({
      initialSettings: assistantConfig.settings,
      initialApiEndpoints: assistantConfig.apiEndpoints,
    });

  const { chatCompletionOptions } = useChatConfig({
    chatSettingsStore,
    config: assistantConfig,
  });

  const isChatLoading = useMemo(() => chatStore.loading, [chatStore.loading]);

  const { packIdFromRoute, sheetIdFromRoute } = routeMatch?.params || {};

  const assistantLogo = useMemo(() => {
    const src = getLogoSrc();
    return (
      <img
        src={src}
        alt={getTitle()}
        style={{
          height: 20,
        }}
      />
    );
  }, []);

  const handleChatError = (e) => {

    let message = e.message || "Something went wrong";

    const meta = {
      error: true,
      statusCode: e.statusCode,
    };

    const chatParentId = chatStore.getParentNodeId();

    const notificationPayload = {
      chatParentId,
      chatSessionId: chatStore.backendSessionId,
      openAIModel: e.model,
    };

    if (e instanceof RequestLimitError) {
      message = `We are currently facing heavy traffic, due to which we couldn't process this request. Please try again in sometime.`;
    }

    if (e.statusCode) {
      let errorCode = `${chatParentId}-${e.statusCode}`;

      if (e.headers) {
        const orgId = e.headers[CHAT_CONVERSATON_ORG_USED_HEADER];
        if (orgId) {
          errorCode += `-${orgId}`;
          notificationPayload.orgIdUsed = orgId;
        }

        meta[CHAT_CONVERSATON_ORG_USED_HEADER] = orgId;
      }

      message += `\n\nError code: ${errorCode}`;
      notificationPayload.errorCode = errorCode;
      notificationPayload.httpStatus = e.statusCode;
    }

    // send email notification
    if (assistantConfig.settings.enableErrorNotification) {
      chatHelpersStore
        .sendAdminNotification(notificationPayload)
        .then(() => {});
    }


    chatStore.addChatMessage({
      role: "assistant",
      type: "message",
      content: message,
      meta,
    });
    chatStore.onError(e);
  };

  async function handleFunctionCall() {
    if (!chatStore.response.functionCall) {
      chatStore.onFinish();
      return;
    }

    const { arguments: args, name } = chatStore.response.functionCall;

    const argumentsObj = JSON.parse(args);
    let nodeId = generateUUID(6),
      parentNodeId = chatStore.getParentNodeId();
    const message = {
      node_id: nodeId,
      parent_row_id: parentNodeId,
      content: JSON.stringify({ name, arguments: argumentsObj }, null, 4),
      name,
      type: "message",
      role: "function",
    };

    chatStore.addLog({ id: chatStore.backendSessionId, ...message });

    chatStore.addChatMessage(message, false, false);
    chatStore.resetChatResponse();
    chatStore.onFinish();

    const functions = {
      createSheet: chatFnsStore.createSheet,
      createSheets: chatFnsStore.createSheets,
      addColumnsToASheet: chatFnsStore.addColumnsToASheet,
      shareSheet: chatFnsStore.shareSheet,
    };

    chatSettingsStore.updateAPiEndpoints({
      chatCompletionUrl: chatCompletionOptions.url,
    });

    if (typeof functions[name] === "function") {
      await functions[name](argumentsObj);
    }
  }

  const handleChatFinish = async () => {
    let nodeId = chatStore.backendSessionId ? generateUUID(6) : 0;
    let parentNodeId = chatStore.backendSessionId
      ? chatStore.getParentNodeId()
      : 0;
    const message = {
      node_id: nodeId,
      parent_row_id: parentNodeId,
      type: "response",
      role: "assistant",
      content: chatStore.response.content,
      functionCall: chatStore.response.functionCall,
    };

    chatStore.addLog({ ...message, id: chatStore.backendSessionId });

    chatStore.addChatMessage(message, false, false);
    chatStore.onFinish();
    chatStore.resetChatResponse();
    inputRef.current?.focus();
  };

  const { fetchResponse, cancelRequest } = useChatCompletion({
    ...chatCompletionOptions,
    onIdle: chatStore.onIdle,
    onStart: chatStore.onStart,
    onResponse: chatStore.onResponse,
    onFinish: handleChatFinish,
    onError: handleChatError,
    onFunctionCall: handleFunctionCall,
  });

  const handleClose = () => {
    // if (loading) return;
    // onClose();
    assistantStore.closeChat();
    cancelRequest();
    chatStore.resetCurrentAction();
  };

  const startNewChat = () => {
    setTokensState(null);
    const id = generateUUID();
    chatStore.setChatSessionId(id);
    chatStore.setBackendSessionId(null);
    chatStore.setSubmitCount(0);
    cancelRequest();
    chatStore.resetChatResponse();
    chatStore.resetChatMessages();
    chatStore.setUpdateMessageIndex(-1);
    assistantStore.setCurrentSheetId(null);
    chatTaskStore.reset();
    inputRef.current?.focus();
  };

  const handleApiKeySubmit = (key) => {
    updateOpenAIApiKey(key);
  };

  const handleApiKeyDelete = () => {
    removeOpenAIApiKey();
  };

  const handleSaveChatSettings = (settings) => {
    chatSettingsStore.saveChatSettings(settings);
  };

  const handleChatSettingsChange = (newChatSettings) => {
    handleSaveChatSettings(newChatSettings);
  };

  const handleBackToMainChat = () => {
    // chatSettingsStore.closeChatSettings();
    assistantStore.changeView("CHAT_HOME");
  };

  const handleChatHistoryItemClick = async (chatHistoryItem) => {
    const { id, backendSessionId } = chatHistoryItem;
    chatStore.setChatSessionId(id);
    chatStore.setBackendSessionId(backendSessionId);
    // Load messages from history
    chatStore.loadChatHistory(backendSessionId);
    chatStore.setUpdateMessageIndex(-1);
    assistantStore.changeView("CHAT_HOME");
  };

  const handleAssistantOpenChange = () => {
    if (!assistantStore.opened) {
      assistantStore.changeView("CHAT_HOME");
      return;
    }

    if (assistantStore.currentPackId !== packIdFromRoute) {
      assistantStore.setCurrentPackId(packIdFromRoute);
    }

    startNewChat();
  };

  const handlePackChange = () => {
    startNewChat();
  };

  // ------------------ RENDER - START ------------------

  const renderDrawerContainer = () => {
    switch (assistantStore.currentView) {
      case "CHAT_SETTINGS": {
        return (
          <ChatSettings
            models={OPENAI_MODELS}
            modelVariants={OPENAI_MODEL_VARIANTS}
            apiKey={apiKey}
            {...settings}
            onBack={handleBackToMainChat}
            onSettingsSubmit={handleChatSettingsChange}
            onApiKeySubmit={handleApiKeySubmit}
            onApiKeyDelete={handleApiKeyDelete}
            setInputProxyInStorage={setKeyValueInLocalStorage}
            inputProxyFromLocalStorage={inputProxy}
          />
        );
      }

      case "CHAT_HISTORY": {
        return (
          <ChatHistory
            onBack={handleBackToMainChat}
            onChatItemClick={handleChatHistoryItemClick}
          />
        );
      }

      case "CHAT_HOME":
      default: {
        return (
          <ChatDrawerHome
            themeColor={packStore.themeColor}
            classes={classes}
            apiKey={apiKey}
            settings={settings}
            chatCompletionOptions={chatCompletionOptions}
            assistantConfig={assistantConfig}
            assistantLogo={assistantLogo}
            packIdFromRoute={packIdFromRoute}
            sheetIdFromRoute={sheetIdFromRoute}
            inputRef={inputRef}
            tokensState={tokensState}
            isChatLoading={isChatLoading}
            onTokensStateChange={setTokensState}
            onNewChatRequest={startNewChat}
            onFetchChatResponse={fetchResponse}
            onClose={handleClose}
          />
        );
      }
    }
  };

  // ------------------ RENDER - END ------------------

  useEffect(() => {
    handleAssistantOpenChange();
  }, [assistantStore.opened]);

  useEffect(() => {
    handlePackChange();
  }, [packIdFromRoute]);

  // useEffect(() => {
  //   const run = async () => {
  //     chatSettingsStore.updateAPiEndpoints({
  //       chatCompletionUrl: chatCompletionOptions.url,
  //     });
  //     await chatFnsStore.createSheets(res);
  //   };

  //   if (assistantStore.opened) {
  //     run();
  //   }
  // }, [assistantStore.opened]);

  return (
    <Drawer
      className={classes.drawer}
      classes={{
        paper: classes.drawerPaper,
      }}
      ModalProps={{
        BackdropProps: {
          className: classes.backdrop,
        },
      }}
      anchor="right"
      open={assistantStore.docked || assistantStore.opened}
      variant={assistantStore.docked ? "permanent" : "temporary"}
      onClose={handleClose}
    >
      <ChatDrawerHeader
        themeColor={packStore.themeColor}
        classes={classes}
        assistantLogo={assistantLogo}
        settings={settings}
        assistantConfig={assistantConfig}
        onClose={handleClose}
      />
      {renderDrawerContainer()}
    </Drawer>
  );
};

export default inject(({ store }) => {
  return {
    chatStore: store.chatStore,
    assistantStore: store.assistantStore,
    chatFnsStore: store.chatFnsStore,
    chatHelpersStore: store.chatHelpersStore,
    chatTaskStore: store.chatTaskStore,
    chatSettingsStore: store.chatSettingsStore,
    packStore: store.packStore,
  };
})(observer(ChatDrawer));
